"use client";

import { c as _c } from "react/compiler-runtime";
import { FiCheck } from "react-icons/fi";

// Same shell, two vocabularies. The approved-product flow talks about
// "reviews" and "clinical experience"; the pipeline flow talks about
// "perspectives" and "pre-launch impressions" because nobody has
// clinical experience with an unapproved therapy yet. Anonymity +
// liability + no-compensation copy is identical between flows.
const COPY = {
  approved: {
    title: "Reviews Disclaimer and Reviewer Agreement",
    anonymityHeading: "Your review will be completely anonymous.",
    disclaimerTail: "Every treatment decision must be individualized and based on a thorough assessment of the patient, current evidence, and approved prescribing information.",
    checkboxes: ["I have direct, personal clinical experience with the product(s) I am reviewing", "I am not receiving, and have not received, any compensation or incentives from pharmaceutical manufacturers, distributors, or any commercial entity in relation to these reviews", "I commit to providing honest, objective, and clinically relevant feedback based solely on my professional experience", "I understand my review will be anonymized and used to generate real-world evidence insights for the verified HCP community"],
    closing: "By checking the boxes above and submitting your review, you acknowledge that you are contributing accurate, experience-based insights to help improve patient care and advance evidence-based medicine."
  },
  pipeline: {
    title: "Perspective Disclaimer and Contributor Agreement",
    anonymityHeading: "Your perspective will be completely anonymous.",
    disclaimerTail: "Every treatment decision must be individualized and based on a thorough assessment of the patient, current evidence, and clinical trial / pre-approval information.",
    checkboxes: ["I am sharing pre-launch impressions and intent, not claims of clinical experience with an unapproved product", "I am not receiving, and have not received, any compensation or incentives from pharmaceutical manufacturers, distributors, or any commercial entity in relation to this perspective", "I commit to providing honest, objective, and clinically relevant feedback based solely on my professional judgment", "I understand my perspective will be anonymized and used to generate pre-launch sentiment signals for the verified HCP community"],
    closing: "By checking the boxes above and submitting your perspective, you acknowledge that you are contributing thoughtful pre-launch impressions to help fellow clinicians understand emerging therapies."
  }
};
const Agreement = t0 => {
  const $ = _c(45);
  const {
    checked,
    toggleCheckbox,
    allChecked,
    onBack,
    onNext,
    isPipeline: t1
  } = t0;
  const isPipeline = t1 === undefined ? false : t1;
  const copy = isPipeline ? COPY.pipeline : COPY.approved;
  let t2;
  if ($[0] !== copy.title) {
    t2 = <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6"><h2 className="text-base sm:text-xl font-bold text-gray-900 font-poppins">{copy.title}</h2></div>;
    $[0] = copy.title;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== copy.anonymityHeading) {
    t3 = <h3 className="text-sm sm:text-base font-semibold text-gray-900 font-poppins mb-1 sm:mb-2">{copy.anonymityHeading}</h3>;
    $[2] = copy.anonymityHeading;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-xs sm:text-sm text-gray-700 font-poppins leading-relaxed">No personally identifiable information will ever be shared with manufacturers, payers, or any third party. Only your specialty, country/region (optional), and years of experience may be displayed in aggregated or de-identified form to provide context for other verified healthcare professionals.</p>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3) {
    t5 = <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-r-lg mb-4 sm:mb-6">{t3}{t4}</div>;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== copy.disclaimerTail) {
    t6 = <p className="text-xs sm:text-sm text-gray-700 font-poppins leading-relaxed">This platform provides verified peer insights intended to support—not replace—your comprehensive clinical judgment. {copy.disclaimerTail}</p>;
    $[7] = copy.disclaimerTail;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="text-xs sm:text-sm text-gray-700 font-poppins mt-2 leading-relaxed">Alpha.MD and MDReviewer disclaim any and all liability for clinical outcomes or decisions arising from the use of information on this platform.</p>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6) {
    t8 = <div className="mb-4 sm:mb-6">{t6}{t7}</div>;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <h3 className="text-sm sm:text-base font-semibold text-gray-900 font-poppins mb-3 sm:mb-4">By proceeding, you confirm and agree to the following:</h3>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== checked || $[14] !== copy.checkboxes || $[15] !== toggleCheckbox) {
    let t11;
    if ($[17] !== checked || $[18] !== toggleCheckbox) {
      t11 = (text, index) => <label key={index} className="flex items-start gap-2 cursor-pointer"><input type="checkbox" checked={checked[index]} onChange={() => toggleCheckbox(index)} className="hidden" /><div className={`w-5 h-5 flex-shrink-0 rounded border-2 transition-all ${checked[index] ? "border-blue-500 bg-blue-500" : "border-gray-300 bg-white"} flex items-center justify-center`}>{checked[index] && <FiCheck className="w-3 h-3 text-white" strokeWidth={3} />}</div><span className="text-sm text-gray-700 font-medium font-poppins leading-relaxed">{text}</span></label>;
      $[17] = checked;
      $[18] = toggleCheckbox;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    t10 = copy.checkboxes.map(t11);
    $[13] = checked;
    $[14] = copy.checkboxes;
    $[15] = toggleCheckbox;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[20] !== t10) {
    t11 = <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">{t10}</div>;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== copy.closing) {
    t12 = <p className="text-[10px] sm:text-xs text-gray-600 font-poppins leading-relaxed">{copy.closing}</p>;
    $[22] = copy.closing;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="text-[10px] sm:text-xs text-gray-600 font-poppins mt-2 leading-relaxed">Thank you for your valuable contribution.</p>;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12) {
    t14 = <div className="bg-gray-50 rounded-lg mb-4 sm:mb-6">{t12}{t13}</div>;
    $[25] = t12;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t11 || $[28] !== t14 || $[29] !== t2 || $[30] !== t5 || $[31] !== t8) {
    t15 = <div className="flex-1 overflow-y-auto hide-scrollbar px-4 sm:px-6 py-3 sm:py-4">{t2}{t5}{t8}{t9}{t11}{t14}</div>;
    $[27] = t11;
    $[28] = t14;
    $[29] = t2;
    $[30] = t5;
    $[31] = t8;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== onBack) {
    t16 = <button onClick={onBack} className="flex-1 border-2 border-gray-300 text-gray-700 text-sm sm:text-base font-semibold font-poppins rounded-lg py-2.5 sm:py-3 hover:bg-gray-50 transition-colors">Cancel</button>;
    $[33] = onBack;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = !allChecked;
  const t18 = `flex-1 text-white rounded-lg text-sm sm:text-base font-semibold font-poppins py-2.5 sm:py-3 transition-all ${allChecked ? "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg transform hover:scale-[1.02]" : "bg-gray-300 cursor-not-allowed"}`;
  let t19;
  if ($[35] !== onNext || $[36] !== t17 || $[37] !== t18) {
    t19 = <button disabled={t17} onClick={onNext} className={t18}>{"Agree & Proceed"}</button>;
    $[35] = onNext;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t16 || $[40] !== t19) {
    t20 = <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-white flex gap-2 sm:gap-3 flex-shrink-0">{t16}{t19}</div>;
    $[39] = t16;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t15 || $[43] !== t20) {
    t21 = <>{t15}{t20}</>;
    $[42] = t15;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  return t21;
};
export default Agreement;